import { useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    activeStructurePeekState,
    peekableStructureState,
} from '~/state/atoms/structureSet/roiAtoms';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { isDialogOpen, isTextEntryElement } from '~/utils/textEntry';

/** The key that peeks under the active structure for as long as it is held. */
export const PEEK_KEY = 'v';

/**
 * Hook: peek under the active structure while the plain V key is held. The
 * peek starts on keydown and ends on keyup. Writes only
 * `activeStructurePeekState`.
 *
 * Start guards (early-return so other handlers still process the event):
 *  - The event is an auto-repeat — a peek that ended while the key stayed
 *    down (the structure was deselected or hidden) stays ended until
 *    the key is pressed afresh, so nothing is withheld again unasked.
 *  - Any modifier (Ctrl/Cmd/Alt/Shift) held — preserves OS/browser shortcuts.
 *  - The active element is an INPUT, TEXTAREA, or contentEditable element.
 *  - A dialog is open: the view behind it is not what the user is on.
 *  - A tool action is in progress: the stroke would go on under a contour
 *    the views no longer draw.
 *  - No structure is peekable.
 *
 * The release is unguarded, because a peek that outlives its key leaves the
 * structure missing from the views with nothing held to explain it: keyup
 * ends the peek with any modifier down and wherever focus is, text fields
 * included.
 */
export function useActiveStructurePeekShortcut(): void {
    const peekable = useRecoilValue(peekableStructureState);
    const setPeek = useSetRecoilState(activeStructurePeekState);
    const toolManager =
        useRecoilValue(viewManagerState)?.interactionContext.toolManager;

    // `KeyboardEvent.code` of the press that started a peek, until that key
    // is released; null otherwise. The code is the physical key, which a
    // modifier pressed during the hold cannot change.
    const heldCodeRef = useRef<string | null>(null);

    // The selector yields a fresh object whenever the structure set is
    // republished; depending on its fields keeps the listeners in place
    // across edits that leave the peekable structure the same.
    const structureSetId = peekable?.structureSetId;
    const roiNumber = peekable?.roiNumber;

    useEffect(() => {
        const endPeek = (): void => {
            heldCodeRef.current = null;
            setPeek(null);
        };

        const onKeyDown = (event: KeyboardEvent): void => {
            // macOS delivers no keyup for a letter released while Cmd is
            // down, so Cmd going down is the last event such a hold is sure
            // to produce.
            if (event.key === 'Meta') {
                endPeek();
                return;
            }
            if (event.key.toLowerCase() !== PEEK_KEY) {
                return;
            }
            if (event.repeat) {
                // The held key keeps repeating wherever focus goes; having
                // started a peek, it must not type into a text field that
                // took focus during the hold.
                if (heldCodeRef.current !== null) {
                    event.preventDefault();
                }
                return;
            }
            // A fresh press: whatever the last one claimed is over, even if
            // its release was never delivered.
            heldCodeRef.current = null;
            if (
                event.ctrlKey ||
                event.metaKey ||
                event.altKey ||
                event.shiftKey
            ) {
                return;
            }
            if (isTextEntryElement(document.activeElement) || isDialogOpen()) {
                return;
            }
            if (toolManager?.isActiveToolActionInProgress()) {
                return;
            }
            if (structureSetId === undefined || roiNumber === undefined) {
                return;
            }
            heldCodeRef.current = event.code;
            setPeek({ structureSetId, roiNumber });
            // Claimed only on the path that consumes it, so the browser's own
            // use of the letter (find-as-you-type) does not run alongside the
            // peek and every refusal above leaves the key untouched.
            event.preventDefault();
        };

        const onKeyUp = (event: KeyboardEvent): void => {
            // A modifier pressed during the hold changes the `key` the
            // release reports (Shift gives 'V', Option+V another character)
            // but not its `code`, so either one identifies the release.
            if (
                event.key.toLowerCase() === PEEK_KEY ||
                (!!heldCodeRef.current && event.code === heldCodeRef.current)
            ) {
                endPeek();
            }
        };

        const listeners = new AbortController();
        const { signal } = listeners;
        window.addEventListener('keydown', onKeyDown, { signal });
        window.addEventListener('keyup', onKeyUp, { signal });
        return () => listeners.abort();
    }, [structureSetId, roiNumber, toolManager, setPeek]);
}
